/**
 * Una caja, un equipo. El servidor amarra cada caja al equipo que la instaló o la usó primero
 * (`app/Domains/Pos` en el backend) y rechaza con `POS.EQUIPO.*` las peticiones de otro equipo.
 *
 * El id del equipo se genera una vez y se guarda en el navegador. Viaja como `?pos_equipo=` y no en
 * un encabezado porque el nginx de producción contesta el preflight de CORS con una lista fija de
 * encabezados. Si un limpiador (CCleaner) borra el navegador, el equipo sale con id nuevo y la caja
 * hay que liberarla en el ERP (Configuración › Cajas).
 */
import appUtils from './app-utils';

const CLAVE_ID = 'pos_equipo_id';
const CLAVE_NOMBRE = 'pos_equipo_nombre';

/** Las rutas que revisa el middleware `pos.equipo`; al resto no se le añade nada. */
const RUTAS_DE_LA_CAJA = /^api\/v1\/(order(\/|$|\?)|cash-turn(\/|$|\?)|subsidiary\/pos-(install|get-info)|pos\/current-turn\/|pos\/cajas\/[^/]+\/soltar|pos\/catalogo\/|pos\/existencias\/)/;

function generarId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  // navegadores sin randomUUID (o sin https): 32 caracteres hexadecimales al azar
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function equipoId() {
  let id = localStorage.getItem(CLAVE_ID);
  if (!id) {
    id = generarId();
    localStorage.setItem(CLAVE_ID, id);
  }
  return id;
}

/** El nombre que quien instala le puso al equipo, o uno sacado del navegador. */
export function nombreDelEquipo() {
  return localStorage.getItem(CLAVE_NOMBRE) || nombreSugerido();
}

export function guardarNombreDelEquipo(nombre) {
  const limpio = (nombre || '').trim();
  if (limpio) {
    localStorage.setItem(CLAVE_NOMBRE, limpio);
  }
}

export function nombreSugerido() {
  const ua = navigator.userAgent || '';
  const navegador = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : 'Navegador';
  const sistema = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /Mac OS/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  return sistema ? `${navegador} en ${sistema}` : navegador;
}

/**
 * Los parámetros que lleva una petición de la caja: el equipo y la caja instalada en él. Sólo en el
 * modo caja (`/pos`); los meseros y la pantalla de cocina no son una caja.
 */
export function paramsDelEquipo(path, store) {
  if (!appUtils.isAppPos() || !RUTAS_DE_LA_CAJA.test(path)) {
    return undefined;
  }
  const params = { pos_equipo: equipoId() };
  const installation = store && store.getters.installation;
  if (installation && installation.cash_id) {
    params.pos_caja = installation.cash_id;
  }
  return params;
}

/** La respuesta dice que la caja de este equipo está (o quedó) en otro equipo. */
export function esRechazoDelEquipo(data) {
  return !!(data && typeof data.code === 'string' && data.code.indexOf('POS.EQUIPO.') === 0);
}

/**
 * Vacía todo lo que la caja guardó en este equipo y la deja sin instalar. Resuelve cuando LokiJS ya
 * guardó (guarda sólo cada 4 s): quien llama recarga la página después, o la caja vieja reaparecería.
 *
 * Todo, no sólo la caja: el catálogo, las listas de precios, los clientes y los impuestos son de la
 * caja anterior. Se comprobó por las malas: vaciando sólo instalación, turno y pedidos, el POS vendió
 * en una empresa un producto de otra.
 */
export async function desinstalarCajaDeEsteEquipo(db, store) {
  const loki = db.adapter && db.adapter.db;
  const colecciones = loki && loki.collections ? loki.collections.map((c) => c.name) : ['installation', 'cash_register_turn', 'orders'];
  for (const coleccion of colecciones) {
    try {
      await db.deleteAll(coleccion);
    } catch (e) {
      console.log('no se pudo vaciar', coleccion, e);
    }
  }
  store.dispatch('setInstallation', null);
  store.commit('setTurn', null);
  store.commit('setEquipoRechazado', false);
  store.dispatch('clearError');
  if (loki && loki.saveDatabase) {
    await new Promise((resolve) => loki.saveDatabase(() => resolve()));
  } else {
    await new Promise((resolve) => setTimeout(resolve, 4500));
  }
}
